import { create } from 'zustand';
import { supabase } from './supabase';
import { format } from 'date-fns';

const DIFFICULTY_TASKS = {
  easy: [
    { id: crypto.randomUUID(), text: 'Meditation', completed: false },
    { id: crypto.randomUUID(), text: 'Work out', completed: false },
    { id: crypto.randomUUID(), text: 'Breath exercises', completed: false }
  ],
  medium: [
    { id: crypto.randomUUID(), text: 'Meditation', completed: false },
    { id: crypto.randomUUID(), text: 'Work out', completed: false },
    { id: crypto.randomUUID(), text: 'Breath exercises', completed: false },
    { id: crypto.randomUUID(), text: 'Visualization', completed: false },
    { id: crypto.randomUUID(), text: 'Vagus nerve exercise', completed: false }
  ],
  hard: [
    { id: crypto.randomUUID(), text: 'Meditation', completed: false },
    { id: crypto.randomUUID(), text: 'Work out', completed: false },
    { id: crypto.randomUUID(), text: 'Breath exercises', completed: false },
    { id: crypto.randomUUID(), text: 'Visualization', completed: false },
    { id: crypto.randomUUID(), text: 'Vagus nerve exercise', completed: false },
    { id: crypto.randomUUID(), text: 'Yoga', completed: false },
    { id: crypto.randomUUID(), text: 'Cold shower', completed: false }
  ]
};

interface AppState {
  session: any;
  userProfile: any;
  entries: any[];
  currentEntry: any;
  isLoading: boolean;
  error: string | null;
  setSession: (session: any) => void;
  setUserProfile: (profile: any) => void;
  setEntries: (entries: any[]) => void;
  setCurrentEntry: (entry: any) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  fetchUserProfile: (userId: string) => Promise<void>;
  fetchEntries: (userId: string) => Promise<void>;
  fetchCurrentEntry: (userId: string, date: Date) => Promise<void>;
  initialize: () => Promise<void>;
  reset: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  session: null,
  userProfile: null,
  entries: [],
  currentEntry: null,
  isLoading: true,
  error: null,
  
  setSession: (session) => set({ session }),
  setUserProfile: (profile) => set({ userProfile: profile }),
  setEntries: (entries) => set({ entries }),
  setCurrentEntry: (entry) => set({ currentEntry: entry }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  fetchUserProfile: async (userId) => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) throw error;
      set({ userProfile: data });
    } catch (error: any) {
      console.error('Error fetching user profile:', error);
      set({ error: 'Failed to load user profile' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchEntries: async (userId) => {
    try {
      set({ isLoading: true, error: null });
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false });

      if (error) throw error;
      set({ entries: data || [] });
    } catch (error: any) {
      console.error('Error fetching entries:', error);
      set({ error: 'Failed to load journal entries' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchCurrentEntry: async (userId, date) => {
    const currentEntry = get().currentEntry;
    const formattedDate = format(date, 'yyyy-MM-dd');
    
    // If we already have the entry for this date, don't fetch again
    if (currentEntry?.date === formattedDate) {
      return;
    }

    try {
      set({ isLoading: true, error: null });
      
      const { data, error } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', userId)
        .eq('date', formattedDate)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!data && format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('difficulty_level')
          .eq('id', userId)
          .single();

        const difficulty = profile?.difficulty_level || 'easy';
        const defaultTasks = DIFFICULTY_TASKS[difficulty as keyof typeof DIFFICULTY_TASKS];

        const { data: newEntry, error: createError } = await supabase
          .from('journal_entries')
          .insert([{
            date: formattedDate,
            user_id: userId,
            tasks: defaultTasks,
            day_rating: 5,
            difficulty_level: difficulty
          }])
          .select()
          .single();

        if (createError) throw createError;
        set({ currentEntry: newEntry });
      } else {
        set({ currentEntry: data });
      }
    } catch (error: any) {
      console.error('Error fetching current entry:', error);
      set({ error: 'Failed to load current entry' });
    } finally {
      set({ isLoading: false });
    }
  },

  initialize: async () => {
    try {
      set({ isLoading: true, error: null });
      const { data: { session } } = await supabase.auth.getSession();
      set({ session });

      if (session?.user) {
        await Promise.all([
          get().fetchUserProfile(session.user.id),
          get().fetchEntries(session.user.id)
        ]);
      }
    } catch (error: any) {
      console.error('Error initializing app:', error);
      set({ error: 'Failed to initialize application' });
    } finally {
      set({ isLoading: false });
    }
  },

  reset: () => {
    set({
      session: null,
      userProfile: null,
      entries: [],
      currentEntry: null,
      isLoading: false,
      error: null
    });
  }
}));

export { DIFFICULTY_TASKS };